/**
 * playcanvas 2.20.1 gsplat 引擎竞态兜底（运行时原型补丁）。
 *
 * 崩溃链：场景卸载（switchTo 传送）时 placement.destroy() 把 resource 置
 * null，placement 的 aabb getter（_aabb ?? resource?.aabb）从此返回
 * undefined；而 world._octreeInstances 表要到「下一帧 render 内部」的
 * reconcile（renderer.update → gsplatDirector.update → reconcile）才会把它移
 * 走。这个空档里 gsplat-world 的 computeGlobalMaxDistance 直接读 aabb.center
 * ——每次触发预算执行（_enforceBudget）都抛 TypeError。异常发生在
 * framerender 阶段（render 之前）：当帧 render() 被跳过，而清理恰恰只在
 * render 里——触发条件连续成立时形成「崩溃帧永不自愈」死循环，
 * _enforceBudget 之后的选档应用每帧被截断，表现为进场景后部分区域持续
 * 空洞（meihuashan 实测：天空盒先出、多处无场景；laomendong2 树小未踩中）。
 *
 * 守卫与引擎自家 _accumulatePlacementAabb 的 if (!a) return 同款——只是
 * computeGlobalMaxDistance 漏写了。安装方式取「运行时拿到真实 world 实例再
 * 改原型」：dev（dbg 构建）与生产构建各自的引擎树都会命中，也避免按子路径
 * deep-import 拉入第二份引擎副本。
 */
import { Vec3 } from 'playcanvas';
import type { Application, BoundingBox, Mat4 } from 'playcanvas';

/** world 内部结构的最小结构视图（引擎不导出 GSplatWorld，按需声明）。 */
interface GsplatWorldInternals {
    _octreeInstances: Map<unknown, {
        placement: {
            node: { getWorldTransform(): Mat4 };
            aabb?: BoundingBox | null;
        };
    }>;
}

type PatchedMethod = (this: GsplatWorldInternals, camera: { getPosition(): Vec3 }) => number;
interface PatchedMethodSlot {
    computeGlobalMaxDistance?: PatchedMethod & { __sogDestroyedPlacementGuard?: boolean };
}

const tempVec3 = new Vec3();
const cameraPosition = new Vec3();

function guardComputeGlobalMaxDistance(this: GsplatWorldInternals, camera: { getPosition(): Vec3 }): number {
    // 与引擎原实现等价（含 Math.max(maxDist, 1) 下限），仅多一个空值跳过。
    let maxDist = 0;
    cameraPosition.copy(camera.getPosition());
    for (const [, inst] of this._octreeInstances) {
        const aabb = inst.placement.aabb;
        // 已销毁 placement：等 render 内 reconcile 清理，跳过而非抛错。
        if (!aabb) continue;
        const worldTransform = inst.placement.node.getWorldTransform();
        worldTransform.transformPoint(aabb.center, tempVec3);
        const scale = worldTransform.getScale().x;
        const dist = tempVec3.distance(cameraPosition) + aabb.halfExtents.length() * scale;
        if (dist > maxDist) maxDist = dist;
    }
    return Math.max(maxDist, 1);
}

function tryPatchWorlds(app: Application): boolean {
    const director = (app.renderer as unknown as {
        gsplatDirector?: {
            camerasMap: Map<unknown, {
                layersMap: Map<unknown, {
                    gsplatManager?: { world?: object };
                    gsplatManagerShadow?: { world?: object };
                }>;
            }>;
        } | null;
    }).gsplatDirector;
    if (!director) return false;
    for (const cameraData of director.camerasMap.values()) {
        for (const layerData of cameraData.layersMap.values()) {
            const world = layerData.gsplatManager?.world ?? layerData.gsplatManagerShadow?.world;
            if (!world) continue;
            const proto = Object.getPrototypeOf(world) as PatchedMethodSlot;
            if (!proto.computeGlobalMaxDistance) {
                console.warn('[gsplat-patch] computeGlobalMaxDistance not found — engine version changed?');
                return true;
            }
            if (proto.computeGlobalMaxDistance.__sogDestroyedPlacementGuard) return true;
            const guarded = guardComputeGlobalMaxDistance as PatchedMethod & { __sogDestroyedPlacementGuard?: boolean };
            guarded.__sogDestroyedPlacementGuard = true;
            proto.computeGlobalMaxDistance = guarded;
            console.info('[gsplat-patch] installed destroyed-placement guard on computeGlobalMaxDistance');
            return true;
        }
    }
    return false;
}

/**
 * 安装补丁并停止轮询即全会话生效（原型级：后续新建的 world——含阴影
 * manager 的——同属一个类，全部命中）。world 要到首个场景首个渲染帧的
 * reconcile 才创建，故低频轮询到命中为止。
 */
export function installGsplatEnginePatches(app: Application): void {
    if (tryPatchWorlds(app)) return;
    const timer = window.setInterval(() => {
        if (tryPatchWorlds(app)) window.clearInterval(timer);
    }, 500);
}
